import { TableCellComponentProps, ColumnConfig, StatusCellType } from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import { Icon } from '@/modules/Icon';
import { TruncatedWithTooltip } from '@/modules/Tooltip';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import {
  getTableCellClasses,
  getTableCellProps,
  handleCellClick,
  getContentAlignClass,
  isFlexColumn,
} from '../../utils/cellStyles';
import { memo } from 'react';

interface StatusValue {
  text: string;
  type: StatusCellType;
}

export const StatusCell = memo(
  ({
    value,
    column,
    state = 'default',
    className,
    onCellFocus,
    stickyLeft = 0,
    isLastLocked = false,
    isRowEditing,
    hasColumnMaxWidthAuto = false,
  }: TableCellComponentProps) => {
    const baseClasses = getTableCellClasses(
      state,
      className,
      column.locked,
      isLastLocked,
      undefined,
      isRowEditing,
      false,
      column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto),
    );
    const cellProps = getTableCellProps(
      column,
      stickyLeft,
      undefined,
      false,
      hasColumnMaxWidthAuto,
    );

    // Determinar si el valor es un objeto con tipo explícito
    const isStatusObject = value && typeof value === 'object' && 'text' in value && 'type' in value;

    // Extraer texto y tipo
    const statusText = isStatusObject ? (value as StatusValue).text : String(value || '');

    // Obtener el tipo de status
    const getStatusType = (): StatusCellType => {
      // 1. Si el valor es un objeto con tipo explícito, usarlo
      if (isStatusObject) {
        return (value as StatusValue).type;
      }

      // 2. Si la columna tiene un statusMap, buscar el valor
      const statusMap = (
        column as ColumnConfig<unknown> & { statusMap?: Record<string, StatusCellType> }
      ).statusMap;
      if (statusMap && statusText in statusMap) {
        return statusMap[statusText];
      }

      // 3. Fallback: inferir del texto (solo como último recurso)
      const normalized = statusText.toLowerCase();
      if (
        normalized.includes('disponible') ||
        normalized.includes('success') ||
        normalized.includes('active') ||
        normalized.includes('activo') ||
        normalized.includes('enabled')
      ) {
        return 'success';
      }
      if (
        normalized.includes('ocupado') ||
        normalized.includes('warning') ||
        normalized.includes('pendiente')
      ) {
        return 'warning';
      }
      if (
        normalized.includes('error') ||
        normalized.includes('danger') ||
        normalized.includes('rechazado')
      ) {
        return 'danger';
      }
      return 'gray';
    };

    const statusType = getStatusType();
    const isSelected = state === 'selected' && !isRowEditing;

    // Colores según Figma para cada tipo y estado usando tokens semánticos
    const statusStyles: Record<
      StatusCellType,
      { default: { bg: string; text: string }; selected: { bg: string; text: string } }
    > = {
      brand: {
        default: {
          bg: 'ui-bg-surface-brand',
          text: 'ui-text-brand',
        },
        selected: {
          bg: 'ui-bg-surface-brand-selected',
          text: 'ui-text-brand-selected',
        },
      },
      success: {
        default: {
          bg: 'ui-bg-surface-success',
          text: 'ui-text-success',
        },
        selected: {
          bg: 'ui-bg-surface-success-selected',
          text: 'ui-text-success-selected',
        },
      },
      warning: {
        default: {
          bg: 'ui-bg-surface-warning',
          text: 'ui-text-warning',
        },
        selected: {
          bg: 'ui-bg-surface-warning-selected',
          text: 'ui-text-warning-selected',
        },
      },
      danger: {
        default: {
          bg: 'ui-bg-surface-danger',
          text: 'ui-text-danger',
        },
        selected: {
          bg: 'ui-bg-surface-danger-selected',
          text: 'ui-text-danger-selected',
        },
      },
      info: {
        default: {
          bg: 'ui-bg-surface-info',
          text: 'ui-text-info',
        },
        selected: {
          bg: 'ui-bg-surface-info-selected',
          text: 'ui-text-info-selected',
        },
      },
      gray: {
        default: {
          bg: 'ui-bg-fill-tertiary',
          text: 'ui-text-primary',
        },
        selected: {
          bg: 'ui-bg-fill-tertiary-selected',
          text: 'ui-text-primary',
        },
      },
    };

    const currentStyle = isSelected
      ? statusStyles[statusType].selected
      : statusStyles[statusType].default;

    // Alineamiento del contenido
    const alignClass = getContentAlignClass(column.contentAlign);
    // Columna con ancho definido → adaptar al ancho (wrap). Sin width → una sola línea, la columna se adapta al contenido.
    const hasFixedWidth = column.width !== undefined;

    return (
      <td className={baseClasses} {...cellProps} onClick={(e) => handleCellClick(e, onCellFocus)}>
        <div
          className={cn(
            'ui-flex ui-h-full ui-items-center',
            alignClass,
            hasFixedWidth && 'ui-min-w-0 ui-overflow-hidden',
          )}
        >
          <div
            className={cn(
              'ui-inline-flex ui-min-h-5 ui-items-center ui-justify-center ui-gap-1.5 ui-rounded-lg ui-py-0.5 ui-pl-1.5 ui-pr-1',
              hasFixedWidth && 'ui-min-w-0 ui-max-w-full ui-overflow-hidden',
              currentStyle.bg,
            )}
          >
            <Icon name={faCircle} size="xs" className={cn('ui-shrink-0', currentStyle.text)} />
            <TruncatedWithTooltip
              truncate={hasFixedWidth}
              title={statusText}
              className={cn(
                'ui-text-body-xs-semibold ui-font-body-xs-semibold ui-leading-body-xs-semibold ui-tracking-body-xs',
                hasFixedWidth ? '' : 'ui-whitespace-nowrap',
                currentStyle.text,
              )}
            >
              {statusText}
            </TruncatedWithTooltip>
          </div>
        </div>
      </td>
    );
  },
);

StatusCell.displayName = 'StatusCell';
